import { useEffect, useState, useRef } from "react";
import { UploadSimple, DownloadSimple, Trash, FilePdf, FileDoc, File } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API, shortDate, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const CATEGORIES = [
  "SOW", "determination_form", "budget", "salary_sheet", "procurement_backup",
  "report", "risk_assessment", "reimbursement", "amendment", "approval_memo", "closeout", "other",
];

function iconFor(name = "") {
  const ext = name.split(".").pop().toLowerCase();
  if (ext === "pdf") return FilePdf;
  if (["doc", "docx"].includes(ext)) return FileDoc;
  return File;
}

export default function DocumentPanel({ relatedType, relatedId, onChange }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("other");
  const [version, setVersion] = useState("v1");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const { data } = await api.get("/documents", { params: { related_type: relatedType, related_id: relatedId } });
    setDocs(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [relatedId]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("related_type", relatedType);
      fd.append("related_id", relatedId);
      fd.append("category", category);
      fd.append("version", version);
      fd.append("tags", tags);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setOpen(false);
      setTags("");
      load();
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const download = (d) => window.open(`${API}/documents/${d.id}/download`, "_blank");

  const remove = async (d) => {
    await api.delete(`/documents/${d.id}`);
    load();
    onChange && onChange();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="overline text-muted-foreground">{docs.length} document(s)</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="upload-doc-btn" size="sm" variant="outline"><UploadSimple size={16} className="mr-1.5" /> Upload</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-head">Upload Document</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>File (Word, PDF, Excel…)</Label>
                <Input data-testid="doc-file" type="file" ref={fileRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.csv,.txt" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Version</Label>
                  <Input data-testid="doc-version" value={version} onChange={(e) => setVersion(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Metadata tags (comma separated)</Label>
                <Input data-testid="doc-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="final, signed, fy26" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="doc-upload-submit" onClick={upload} disabled={busy}>{busy ? "Uploading…" : "Upload"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {docs.length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-8 text-center text-sm text-muted-foreground">
          No documents yet.
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => {
            const Icon = iconFor(d.file_name);
            return (
              <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center gap-3 border border-border rounded-md p-3 bg-card">
                <Icon size={26} className="text-primary shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{d.file_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {d.category.replace(/_/g, " ")} · {d.version} · {d.uploaded_by_name} · {shortDate(d.upload_date)}
                  </div>
                  {d.tags?.length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {d.tags.map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
                    </div>
                  )}
                </div>
                <Button size="icon" variant="ghost" data-testid={`doc-download-${d.id}`} onClick={() => download(d)}><DownloadSimple size={16} /></Button>
                <Button size="icon" variant="ghost" data-testid={`doc-delete-${d.id}`} onClick={() => remove(d)}><Trash size={16} className="text-alert" /></Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
